import { useState, useEffect, useCallback } from 'react';
import { Loader2, Mail } from 'lucide-react';
import { arDunningApi } from '../../services/arDunningApi';
import { useToast } from '../ui/Toast';
import { formatDateTime } from '../../utils/arDunningFormat';
import type { ArEmailHistoryEntry } from '../../types/arDunning';

/**
 * Dunning e-mail history from [Reporting].[EmailLog]. Reminders are recorded here
 * once the Phase-4 e-mail feature goes live; until then this typically shows the
 * empty state.
 */
export function DunningHistoryTab() {
    const { toast } = useToast();
    const [emails, setEmails] = useState<ArEmailHistoryEntry[]>([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await arDunningApi.listEmailHistory({ limit: 300 });
            setEmails(res.emails);
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Failed to load history', 'error');
        } finally {
            setLoading(false);
        }
    }, [toast]);

    useEffect(() => { void load(); }, [load]);

    const recipients = (e: ArEmailHistoryEntry) => {
        const to = e.to.map(r => r.name || r.email).filter(Boolean).join(', ');
        return to || '–';
    };

    if (loading) return <div className="py-10 text-center text-slate-400"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>;

    if (emails.length === 0) {
        return (
            <div className="rounded-lg border border-dashed border-slate-200 py-12 text-center">
                <Mail className="mx-auto h-8 w-8 text-slate-300" />
                <p className="mt-2 text-sm font-medium text-slate-500">No dunning e-mails sent yet</p>
                <p className="mt-1 text-xs text-slate-400">Sent reminders will appear here once the e-mail feature (Phase 4) is live.</p>
            </div>
        );
    }

    return (
        <div className="max-h-[65vh] overflow-auto rounded-lg border border-slate-200">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 [&_th]:sticky [&_th]:top-0 [&_th]:z-10 [&_th]:bg-slate-50">
                    <tr>
                        <th className="px-3 py-2">Sent</th>
                        <th className="px-3 py-2">By</th>
                        <th className="px-3 py-2">Audience</th>
                        <th className="px-3 py-2">Subject</th>
                        <th className="px-3 py-2">To</th>
                        <th className="px-3 py-2">Ref</th>
                        <th className="px-3 py-2">Status</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                    {emails.map(e => (
                        <tr key={e.Id} className="hover:bg-slate-50">
                            <td className="whitespace-nowrap px-3 py-2 text-slate-500">{formatDateTime(e.SentAt)}</td>
                            <td className="whitespace-nowrap px-3 py-2 text-slate-600">{e.SentByEmail}</td>
                            <td className="px-3 py-2">{e.audience ? <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${e.audience === 'customer' ? 'bg-rose-100 text-rose-700' : 'bg-slate-100 text-slate-600'}`}>{e.audience}</span> : '–'}</td>
                            <td className="max-w-xs truncate px-3 py-2 text-slate-700" title={e.Subject}>{e.Subject}</td>
                            <td className="max-w-xs truncate px-3 py-2 text-slate-500" title={recipients(e)}>{recipients(e)}</td>
                            <td className="whitespace-nowrap px-3 py-2 text-xs text-slate-400">{e.ContextKey ?? '–'}</td>
                            <td className="px-3 py-2">
                                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${e.Success ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>{e.Success ? 'sent' : 'failed'}</span>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
